import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MONTH_NAMES_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

const formatMonthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const humanizeMonth = (key) => {
  if (!key) return "";
  const [y, m] = key.split("-");
  return `${MONTH_NAMES_ES[parseInt(m, 10) - 1]} de ${y}`;
};

/**
 * State + persistence for the monthly newsletter draft.
 *
 * Marta picks a `target_month` (default = current month, or next month if we
 * are in the second half), fetches her saved draft if any, edits subject + body,
 * and toggles the approval flag.
 *
 * Approval makes the cron at day 1 09:00 Europe/Madrid send the draft
 * automatically to every confirmed subscriber.
 */
export const useMonthlyDraft = (password, loggedIn) => {
  const now = new Date();
  const defaultMonth = now.getDate() >= 15 ? new Date(now.getFullYear(), now.getMonth() + 1, 1) : now;
  const [targetMonth, setTargetMonth] = useState(formatMonthKey(defaultMonth));

  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchDraft = useCallback(async () => {
    if (!loggedIn) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/admin/draft/get`, { password, target_month: targetMonth });
      setDraft(data.draft);
      setSubject(data.draft?.subject || "");
      setBodyHtml(data.draft?.body_html || "");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar el borrador");
    } finally {
      setLoading(false);
    }
  }, [password, targetMonth, loggedIn]);

  useEffect(() => { fetchDraft(); }, [fetchDraft]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/admin/draft/save`, {
        password, target_month: targetMonth, subject, body_html: bodyHtml,
      });
      setDraft(data.draft);
      toast.success("Borrador guardado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const toggleApproval = async () => {
    if (!draft) {
      toast.error("Guarda el borrador antes de aprobarlo");
      return;
    }
    const newApproved = draft.status !== "approved";
    if (newApproved) {
      const confirm = window.confirm(
        `¿Aprobar la newsletter de ${humanizeMonth(targetMonth)} para envío automático?\n\n` +
        `Se enviará SOLA el día 1 de ${humanizeMonth(targetMonth).split(" de ")[0]} a las 09:00 a todas las suscriptoras confirmadas.\n\n` +
        `Puedes desaprobarla en cualquier momento si cambias de opinión.`
      );
      if (!confirm) return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/admin/draft/approve`, {
        password, target_month: targetMonth, approved: newApproved,
      });
      setDraft(data.draft);
      toast.success(newApproved ? "Aprobada para envío automático" : "Desaprobada · vuelve a borrador");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return {
    targetMonth, setTargetMonth,
    monthLabel: humanizeMonth(targetMonth),
    subject, setSubject,
    bodyHtml, setBodyHtml,
    draft, loading, saving,
    save, toggleApproval, refresh: fetchDraft,
  };
};

export default useMonthlyDraft;
